import { useEffect, useState } from 'react'
import { Copy, Pin, PinOff } from 'lucide-react'
import type { Tag, TimeEntry } from '#shared/types'
import type { Project } from '#shared/projects'
import type { Pinned } from '#shared/recent'
import { projectName } from '#shared/projects'
import { formatHours } from '#shared/duration'
import { orderPins, pinKey, withoutPinned } from '#shared/recent'
import { readPins, writePins } from '../lib/prefs'

/** What the form needs to be filled in. The date is not part of it. */
export interface Shortcut {
  projectId: string
  tagIds: string[]
  minutes: number
  notes: string | null
}

interface Props {
  /** Newest first, already thinned to distinct shortcuts by the API. */
  entries: TimeEntry[]
  projects: Project[]
  tags: Tag[]
  /** Hands the shortcut's project, tags, hours and note to the form above. */
  onCopy: (shortcut: Shortcut) => void
}

/** A row in the list: a pin somebody kept, or one of the recent entries. */
interface Row extends Shortcut {
  key: string
  pinnedAt: string | null
}

/**
 * The last few things this person logged, ready to log again.
 *
 * Most timesheet entries are a repeat of an earlier one — the same standup,
 * the same project at the same length — and re-picking a project, two tags
 * and a duration to say so is most of the work of using this app. A copy is
 * one button.
 *
 * One thin full-width row each, everything on a single line: the project,
 * its tags, the note, the hours and the button. Five of them are a short
 * list to scan down, not five objects to look at — and the note is there to
 * recognise a shortcut by, so it truncates rather than wrapping the row
 * into a block.
 *
 * Deliberately no date on a card, and none copied. The day comes from the
 * picker above the form — copying yesterday's entry is how you log it
 * against today, which is the whole point.
 *
 * Above the recent entries sit the pinned ones: shortcuts somebody chose to
 * keep, which do not fall off the list as they log other things. They are
 * held in a cookie rather than the database — a pin is how one person likes
 * their own form filled in, not team data.
 */
export function RecentEntries({ entries, projects, tags, onCopy }: Props) {
  /**
   * The rows on screen, settled when the entries arrive and left alone
   * afterwards.
   *
   * Pinning and unpinning change the cookie and the button, never the list:
   * a row that moved or vanished under the cursor the moment it was clicked
   * would make the button hard to use and easy to misfire. The next load
   * puts everything in its place.
   */
  const [rows, setRows] = useState<Row[]>([])

  /** The keys currently in the cookie — what each button offers to do. */
  const [pinnedKeys, setPinnedKeys] = useState<Set<string>>(new Set())

  /** A pin that could not be stored. Silence would be worse than a line. */
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    const pins = readPins()
    setPinnedKeys(new Set(pins.map(pinKey)))

    const pinnedRows: Row[] = orderPins(pins).map((pin) => ({
      key: `pin:${pin.pinnedAt}:${pinKey(pin)}`,
      projectId: pin.projectId,
      tagIds: pin.tagIds,
      minutes: pin.minutes,
      notes: pin.notes,
      pinnedAt: pin.pinnedAt,
    }))

    const recentRows: Row[] = withoutPinned(entries, pins).map((entry) => ({
      key: entry.id,
      projectId: entry.projectId,
      tagIds: entry.tagIds,
      minutes: entry.minutes,
      notes: entry.notes ?? null,
      pinnedAt: null,
    }))

    setRows([...pinnedRows, ...recentRows])
  }, [entries])

  if (rows.length === 0) return null

  // Position in the manager's order, so the chips read the same way here as
  // in the picker the copy will fill.
  const rank = new Map(tags.map((t, index) => [t.id, index]))
  const names = new Map(tags.map((t) => [t.id, t.name]))

  const byId = new Map(projects.map((p) => [p.id, p]))

  const tagLabels = (row: Row) =>
    [...row.tagIds]
      .sort((a, b) => (rank.get(a) ?? Infinity) - (rank.get(b) ?? Infinity))
      .map((id) => names.get(id) ?? id)

  /**
   * A pin outlives the things it names. Its project can be archived and its
   * tags withdrawn between the pin and the click, and the form would refuse
   * the copy — so the shortcut is trimmed to what can still be logged, and
   * one whose project is gone is not offered at all.
   */
  const copyable = (row: Row): Shortcut | null => {
    const project = byId.get(row.projectId)
    if (!project || project.archived) return null

    const usable = project.allowsTags
      ? row.tagIds.filter((id) => tags.some((tag) => tag.id === id && !tag.archived))
      : []

    return { projectId: row.projectId, tagIds: usable, minutes: row.minutes, notes: row.notes }
  }

  function togglePin(row: Row) {
    const key = pinKey(row)
    const pins = readPins()
    const next: Pinned[] = pinnedKeys.has(key)
      ? pins.filter((pin) => pinKey(pin) !== key)
      : [
          ...pins,
          {
            projectId: row.projectId,
            tagIds: row.tagIds,
            minutes: row.minutes,
            notes: row.notes,
            pinnedAt: new Date().toISOString(),
          },
        ]

    const stored = writePins(next)
    setFailed(!stored)
    setPinnedKeys(new Set(readPins().map(pinKey)))
  }

  return (
    <div className="card">
      <div className="card-head">
        <h2>Recent Entries</h2>
      </div>

      {failed && <p className="error">Your browser would not store that pin.</p>}

      <ul className="recent-rows">
        {rows.map((row) => {
          const project = projectName(projects, row.projectId)
          const labels = tagLabels(row)
          const shortcut = copyable(row)
          const isPinned = pinnedKeys.has(pinKey(row))
          const named = `${formatHours(row.minutes)} on ${project}${
            labels.length ? `, tagged ${labels.join(' and ')}` : ''
          }`

          return (
            // The tint and the marker follow the cookie, not the row's
            // position: unpinning leaves the row where it is until the
            // next load, and it should stop looking pinned the moment it
            // stops being pinned.
            <li key={row.key} className={`recent-row${isPinned ? ' is-pinned' : ''}`}>
              {/* One shrinking run: whatever does not fit is truncated from
                  the note end, so the project and the hours are the last
                  things to go. */}
              <span className="recent-line">
                {isPinned && <Pin size={13} className="recent-mark" aria-label="Pinned" />}

                <span className="recent-project">{project}</span>

                {labels.length > 0 && (
                  <span className="tag-list">
                    {labels.map((name) => (
                      <span key={name} className="tag-badge">
                        {name}
                      </span>
                    ))}
                  </span>
                )}

                {row.notes && <span className="recent-note">{row.notes}</span>}
              </span>

              <span className="recent-hours">{formatHours(row.minutes)}</span>

              <button
                type="button"
                className="chip recent-pin"
                onClick={() => togglePin(row)}
                aria-pressed={isPinned}
                aria-label={`${isPinned ? 'Unpin' : 'Pin'} ${named}`}
              >
                {isPinned ? (
                  <PinOff size={14} aria-hidden="true" />
                ) : (
                  <Pin size={14} aria-hidden="true" />
                )}
                {isPinned ? 'Unpin' : 'Pin'}
              </button>

              <button
                type="button"
                className="chip recent-copy"
                onClick={() => shortcut && onCopy(shortcut)}
                disabled={shortcut === null}
                // The visible word is just "Copy" on near-identical rows, so
                // the name has to say which one — and it names the whole
                // shortcut, which the row itself may have truncated.
                aria-label={
                  shortcut
                    ? `Copy ${named} into the form`
                    : `${project} has been archived, so ${named} cannot be copied`
                }
              >
                <Copy size={14} aria-hidden="true" />
                Copy
              </button>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
